import { Anchor, Center, Group, Loader, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FilterMultiSelect } from '@/components/FilterMultiSelect';
import { SearchInput } from '@/components/SearchInput';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { usePermissions } from '../../contexts/PermissionContext';
import {
  type CaseloadAggregatedContactRow,
  useMedplumCaseloadAggregatedContacts,
} from '../../hooks/useMedplumPatientContacts';
import {
  CASELOAD_CONTACT_TYPE_OPTIONS,
  caseloadContactRelationships,
  caseloadContactTypesFromRow,
  contactRowMatchesRelationshipFilter,
  contactRowMatchesSearch,
  contactRowMatchesTypeFilter,
  formatCaseloadContactTypes,
  type CaseloadContactType,
} from './caseloadContactTypes';
import { CaseloadTabPanelLayout } from './components/CaseloadTabPanelLayout';
import {
  PROVIDER_FILTER_MY_CLIENTS,
  PROVIDER_FILTER_MY_TEAM,
  defaultProviderFilterForRole,
  getEpisodeRows,
  providerFilterOptionsFromRows,
  resolveAllByDefaultFilter,
  subjectClientIdsMatchingProviderFilter,
  type Client,
  type ClientGroup,
} from './data';

const DEFAULT_CONTACT_TYPE_FILTER = CASELOAD_CONTACT_TYPE_OPTIONS.map((o) => o.value);

type ContactTableRow = CaseloadAggregatedContactRow & {
  contactTypes: CaseloadContactType[];
  contactTypeSummary: string;
  relationships: string[];
};

export interface CaseloadContactsPageProps {
  /** Same client list as Caseload → Clients (episode-scoped Patient rows). */
  clients: Client[];
  clientGroups?: ClientGroup[];
  /** True while initial caseload Patient list is loading. */
  caseloadLoading?: boolean;
}

function formatCell(value: string | undefined): string {
  const t = value?.trim();
  return t || '—';
}

/**
 * Caseload → Contacts: Patient.contact rows, active RelatedPersons, and episode guarantors
 * for every client on the caseload list (same source as the Clients tab).
 */
export function CaseloadContactsPage({
  clients,
  clientGroups = [],
  caseloadLoading,
}: CaseloadContactsPageProps): JSX.Element {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const { practitioner: viewerPractitioner } = useCurrentUserPractitioner();
  const [searchQuery, setSearchQuery] = useState('');
  /** null = untouched. Resolved per render so the role default still applies once RBAC loads. */
  const [providerSelection, setProviderSelection] = useState<string[] | null>(null);
  const providerFilter = providerSelection ?? defaultProviderFilterForRole(role);
  /** null = untouched, so relationships that load later stay included. */
  const [relationshipSelection, setRelationshipSelection] = useState<string[] | null>(null);
  const [contactTypeFilter, setContactTypeFilter] = useState<string[]>(DEFAULT_CONTACT_TYPE_FILTER);

  const clientSlice = useMemo(
    () => clients.map((c) => ({ id: c.id, name: c.name })),
    [clients],
  );
  const { rows, loading: contactsLoading } = useMedplumCaseloadAggregatedContacts(clientSlice);

  const allEpisodeRows = useMemo(
    () => getEpisodeRows(clients, clientGroups),
    [clients, clientGroups],
  );

  const providerOptions = useMemo(
    () => [
      { value: PROVIDER_FILTER_MY_CLIENTS, label: 'My Clients' },
      { value: PROVIDER_FILTER_MY_TEAM, label: 'My Team' },
      ...providerFilterOptionsFromRows(allEpisodeRows, viewerPractitioner?.id),
    ],
    [allEpisodeRows, viewerPractitioner?.id],
  );

  const tableRows = useMemo(
    (): ContactTableRow[] =>
      rows.map((row) => {
        const contactTypes = caseloadContactTypesFromRow(row);
        return {
          ...row,
          contactTypes,
          contactTypeSummary: formatCaseloadContactTypes(contactTypes),
          relationships: caseloadContactRelationships(row),
        };
      }),
    [rows],
  );

  const relationshipOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of tableRows) {
      for (const rel of row.relationships) names.add(rel);
    }
    return [...names].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value }));
  }, [tableRows]);

  const relationshipFilter = resolveAllByDefaultFilter(relationshipSelection, relationshipOptions);

  const matchingClientIds = useMemo(
    () =>
      subjectClientIdsMatchingProviderFilter(
        clients,
        clientGroups,
        providerFilter,
        viewerPractitioner?.id,
      ),
    [clients, clientGroups, providerFilter, viewerPractitioner?.id],
  );

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim();
    return tableRows.filter((row) => {
      if (!matchingClientIds.has(row.subjectPatientId)) return false;
      if (!contactRowMatchesTypeFilter(row.contactTypes, contactTypeFilter)) return false;
      if (!contactRowMatchesRelationshipFilter(row.relationships, relationshipFilter)) return false;
      if (query && !contactRowMatchesSearch(row, query, row.contactTypeSummary)) return false;
      return true;
    });
  }, [tableRows, matchingClientIds, contactTypeFilter, relationshipFilter, searchQuery]);

  const columns = useMemo(
    (): UnifyTableColumn<ContactTableRow>[] => [
      {
        key: 'subjectClientName',
        label: 'Client',
        sortable: true,
        render: (row) => (
          <Anchor
            component={Link}
            to={`/dashboard/clients/${row.subjectPatientId}`}
            size="sm"
            fw={500}
            c="gold.7"
            underline="hover"
            style={{ textUnderlineOffset: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {row.subjectClientName}
          </Anchor>
        ),
        getValue: (row) => row.subjectClientName,
      },
      {
        key: 'displayName',
        label: 'Contact',
        sortable: true,
        render: (row) => {
          if (row.clientChartId) {
            return (
              <Anchor
                component={Link}
                to={`/dashboard/clients/${row.clientChartId}`}
                size="sm"
                fw={500}
                c="gold.7"
                underline="hover"
                style={{ textUnderlineOffset: 2 }}
                onClick={(e) => e.stopPropagation()}
              >
                {row.displayName}
              </Anchor>
            );
          }
          return (
            <Text size="sm" fw={600}>
              {row.displayName}
            </Text>
          );
        },
        getValue: (row) => row.displayName,
      },
      {
        key: 'rolesSummary',
        label: 'Relationship',
        sortable: true,
        render: (row) => <Text size="sm">{formatCell(row.rolesSummary)}</Text>,
        getValue: (row) => row.rolesSummary ?? '',
      },
      {
        key: 'contactType',
        label: 'Contact type',
        sortable: true,
        render: (row) => (
          <Text size="sm">{formatCell(row.contactTypeSummary)}</Text>
        ),
        getValue: (row) => row.contactTypeSummary,
      },
      {
        key: 'phone',
        label: 'Phone',
        sortable: true,
        render: (row) => <Text size="sm">{formatCell(row.phone)}</Text>,
        getValue: (row) => row.phone ?? '',
      },
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        render: (row) => <Text size="sm">{formatCell(row.email)}</Text>,
        getValue: (row) => row.email ?? '',
      },
    ],
    [],
  );

  const handleRowClick = (row: ContactTableRow) => {
    navigate(`/dashboard/clients/${row.subjectPatientId}/profile?tab=contacts`);
  };

  if (caseloadLoading && clients.length === 0) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  const showContactsSpinner = contactsLoading && rows.length === 0;

  return (
    <CaseloadTabPanelLayout
      filters={
        <>
          <SearchInput
            placeholder="Search clients or contacts…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
          />
          <FilterMultiSelect
            allLabel="All Providers"
            data={providerOptions}
            value={providerFilter}
            onChange={setProviderSelection}
            selectedLabel="Selected"
            optionsAboveDivider={['my-clients', 'supervisee-caseload']}
          />
          <FilterMultiSelect
            allLabel="All Relationships"
            data={relationshipOptions}
            value={relationshipFilter}
            onChange={setRelationshipSelection}
          />
          <FilterMultiSelect
            allLabel="All Contact Types"
            data={CASELOAD_CONTACT_TYPE_OPTIONS}
            value={contactTypeFilter}
            onChange={setContactTypeFilter}
          />
        </>
      }
    >
      {showContactsSpinner ? (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      ) : !caseloadLoading && clients.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No clients on your caseload yet. When clients appear on the Clients tab, their contacts will show here.
        </Text>
      ) : filteredRows.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No contacts found matching your criteria
        </Text>
      ) : (
        <UnifyTable<ContactTableRow>
          withPaper={false}
          searchable={false}
          columns={columns}
          data={filteredRows}
          getRowKey={(r) => r.rowKey}
          sortable
          pagination
          defaultRowsPerPage={50}
          onRowClick={handleRowClick}
          tableProps={{ highlightOnHover: true }}
        />
      )}
      {contactsLoading && rows.length > 0 ? (
        <Group justify="flex-end" mt="sm">
          <Text size="xs" c="dimmed">
            Refreshing…
          </Text>
        </Group>
      ) : null}
    </CaseloadTabPanelLayout>
  );
}
